import React, { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  NumberInput,
  Textarea,
  Button,
  Group,
  Text,
  Alert,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import { useResponsive } from "../../context/ResponsiveContext";
import { useProducts } from "../../context/ProductsContext";
import { ProductQuantityAdjustment } from "../../types/productQuantityAdjustments";

interface EditQuantityAdjustmentModalProps {
  opened: boolean;
  onClose: () => void;
  productId: string;
  adjustment: ProductQuantityAdjustment;
  onSuccess: () => void;
}

export default function EditQuantityAdjustmentModal({
  opened,
  onClose,
  productId,
  adjustment,
  onSuccess,
}: EditQuantityAdjustmentModalProps) {
  const { updateProductStockAdjustment } = useApp();
  const { products } = useProducts();
  const { isMobile } = useResponsive();

  // Find current product to get current stock
  const currentProduct = products.find((p) => p.id === productId);
  const currentStock = currentProduct?.quantity ?? 0;

  const [adjustmentAmount, setAdjustmentAmount] = useState<number | "">(
    adjustment.adjustment,
  );
  const [notes, setNotes] = useState(adjustment.notes || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when adjustment changes
  useEffect(() => {
    setAdjustmentAmount(adjustment.adjustment);
    setNotes(adjustment.notes || "");
  }, [adjustment]);

  const handleClose = () => {
    if (!isSubmitting) {
      setError(null);
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (adjustmentAmount === "" || adjustmentAmount === 0) {
      setError("Adjustment amount cannot be zero");
      return;
    }

    // Calculate what the stock would be after editing this adjustment
    // Current stock already includes this adjustment, so we need to:
    // 1. Remove the old adjustment: currentStock - adjustment.adjustment
    // 2. Add the new adjustment: (currentStock - adjustment.adjustment) + adjustmentAmount
    const stockWithoutThisAdjustment = currentStock - adjustment.adjustment;
    const newAdjustmentAmount = Number(adjustmentAmount);
    const resultingStock = stockWithoutThisAdjustment + newAdjustmentAmount;

    // Validate that the resulting stock won't be negative
    if (resultingStock < 0) {
      const availableStock = stockWithoutThisAdjustment;
      if (newAdjustmentAmount < 0) {
        setError(
          `Cannot remove ${Math.abs(newAdjustmentAmount)} units. Only ${availableStock} units available after removing the current adjustment.`,
        );
      } else {
        setError(
          `This adjustment would result in ${resultingStock} units, which is below zero. Only ${availableStock} units available after removing the current adjustment.`,
        );
      }
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      await updateProductStockAdjustment(adjustment.id, {
        adjustment: newAdjustmentAmount,
        notes: notes.trim() || undefined,
      });

      handleClose();
      onSuccess();
    } catch (err) {
      console.error("Error updating quantity adjustment:", err);
      setError(
        err instanceof Error ? err.message : "Failed to update adjustment",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Edit Quantity Adjustment"
      size="md"
      fullScreen={isMobile}
      centered
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {error && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Error"
              color="red"
            >
              {error}
            </Alert>
          )}

          <NumberInput
            label="Adjustment Amount"
            description="Enter positive number to add stock, negative to remove stock"
            placeholder="e.g., 10 or -5"
            value={adjustmentAmount}
            onChange={(value) => {
              if (typeof value === "number") {
                setAdjustmentAmount(value);
              } else if (value === "") {
                setAdjustmentAmount("");
              }
            }}
            required
            allowDecimal={false}
            min={undefined}
            autoFocus
          />

          <Textarea
            label="Notes (Optional)"
            placeholder="e.g., Received shipment, Damaged items removed, etc."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            minRows={3}
            maxRows={6}
          />

          <Group justify="flex-end" gap="sm" mt="md">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting}>
              Save Changes
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
